/*
 * Cart drawer — "You may also like" recommendations.
 *
 * Hydrates [data-cart-recommendations] placeholders rendered server-side
 * in cart-drawer.liquid. Fetches Shopify's native complementary
 * recommendations API for the most-recent cart item; falls back to
 * intent=related when complementary returns nothing (small DTC catalogs
 * sometimes have sparse complementary signals).
 *
 * Request budget: nothing is fetched until the drawer is OPEN (it used to
 * cost /cart.js + 2 recommendation calls on every page view with a
 * non-empty cart), results are cached per market + seed product for the
 * session, and products already in the cart are read from the
 * placeholder's data-cart-product-ids instead of a /cart.js round trip.
 * The several re-hydrate triggers below are therefore cheap: a re-rendered
 * placeholder is filled from the cache with no network.
 *
 * Client-side title-split mirrors snippets/_split-product-title.liquid —
 * the FRAGRANCES list must stay in sync with that snippet (and with
 * the now-removed assets/rebuy-cart-title-split.js).
 *
 * ATC: POSTs to /cart/add.js with sections=['cart-drawer','cart-icon-
 * bubble'], then calls cartDrawer.renderContents(parsedState) which is
 * Dawn's existing post-ATC re-render method.
 *
 * v2 backlog: rule-based "matching cream" recommendations via a
 * cart_recommendations metaobject — would override Shopify's complementary
 * picks for hand-curated fragrance↔cream/candle pairings.
 */
(function () {
  'use strict';

  /* Mirrors snippets/_split-product-title.liquid. Keep in sync. */
  var FRAGRANCES = [
    'Tea in the Sahara', 'Whispering Pine', 'Campfire Stereo', 'Palace Intrigue',
    'Mandarin Crush', 'In Full Bloom', 'Jardín Azteca', 'Reading Room',
    'Inner Circle', 'Wicked Ficus', 'Desert Bloom', 'Dusk in Eden',
    'Pumice Stone', 'Kiso Forest', 'Disco Lily', 'Wild Pear',
    'Awakening', 'Commodore', 'Genovese', 'Olympus', 'Renewal', 'Acadia'
  ];

  function escapeHtml(s) {
    return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function splitTitle(title) {
    if (!title) return '';
    for (var i = 0; i < FRAGRANCES.length; i++) {
      var f = FRAGRANCES[i];
      if (title.indexOf(f) === 0 && title.length > f.length) {
        var rest = title.slice(f.length).trim();
        if (rest) return escapeHtml(f) + '<br>' + escapeHtml(rest);
      }
    }
    return escapeHtml(title);
  }

  /* Mirrors snippets/_cart-drawer-rec-tile.liquid: `money | remove: '.00'`
     ($22.00 -> $22, $19.99 stays $19.99) + the AUD "A$" money_symbol rule.
     The locale is pinned per currency so the SHOPPER's browser locale can't
     change the output (an en-GB browser printed USD as "US$27", a de-DE one
     GBP as "27 £"). en-US emits "A$" for AUD and "CA$" for CAD, so CAD uses
     en-CA to get the bare "$" Liquid prints. */
  var MONEY_LOCALES = { USD: 'en-US', GBP: 'en-GB', AUD: 'en-US', CAD: 'en-CA', EUR: 'en-IE', NZD: 'en-NZ' };
  var MONEY_SYMBOLS = { USD: '$', GBP: '£', AUD: 'A$', CAD: '$', EUR: '€', NZD: '$' };
  function formatMoney(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    var amount = Math.round(cents) / 100;
    var fraction = Math.round(cents) % 100 === 0 ? 0 : 2;
    try {
      return new Intl.NumberFormat(MONEY_LOCALES[currency] || 'en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: fraction,
        maximumFractionDigits: fraction
      }).format(amount);
    } catch (e) {
      var symbol = MONEY_SYMBOLS[currency];
      var number = amount.toFixed(fraction);
      return symbol ? symbol + number : number + ' ' + currency;
    }
  }

  function imageUrl(featured, width) {
    if (!featured) return '';
    var w = width || 400;
    if (featured.indexOf('?') >= 0) return featured + '&width=' + w;
    return featured + '?width=' + w;
  }

  /* Market root ('/', '/en-gb/', '/en-au/'). Shopify's content_for_header sets
     Shopify.routes.root on every storefront page. Unprefixed endpoints resolve in
     the session's cookie market, and their product.url values come back as
     '/products/...'. Following such a link moved GB/AU shoppers into the US store
     and switched their cart to USD. */
  function marketRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return typeof root === 'string' && root ? root : '/';
  }

  /* Belt-and-braces: prefix any root-relative URL that lacks the market root. */
  function marketUrl(url) {
    var root = marketRoot();
    if (!url || root === '/' || url.charAt(0) !== '/' || url.indexOf(root) === 0) return url;
    return root + url.replace(/^\//, '');
  }

  function hasTag(product, tag) {
    var tags = product.tags;
    if (!tags) return false;
    if (typeof tags === 'string') tags = tags.split(',').map(function (t) { return t.trim(); });
    return tags.indexOf(tag) >= 0;
  }

  /* Price markup, mirroring the Liquid tile: while a sale is live
     (data-sale-pct1 on the placeholder) eligible viewers see the tier-1
     price over the struck regular price, gated viewers the regular price.
     Exclusions follow the sale Function as far as the product JSON allows
     (`_no_sale` tag, requires_selling_plan). Same round-half-up math as
     price.liquid: price × (100 − pct) / 100, rounded. */
  function priceMarkup(price, compareAt, salePct, product) {
    var regular = escapeHtml(formatMoney(price));
    var compareMarkup = '';
    if (compareAt && compareAt > price) {
      compareMarkup = ' <s>' + escapeHtml(formatMoney(compareAt)) + '</s>';
    }
    var saleOk = salePct > 0 && !hasTag(product, '_no_sale') && !product.requires_selling_plan;
    if (!saleOk) return regular + compareMarkup;
    var salePrice = Math.round(price * (100 - salePct) / 100);
    return (
      '<span class="cart-drawer__rec-price-sale sale-event--eligible-only">' +
        escapeHtml(formatMoney(salePrice)) + ' <s>' + regular + '</s>' +
      '</span>' +
      '<span class="sale-event--gated-only">' + regular + compareMarkup + '</span>'
    );
  }

  function buildTile(product, salePct) {
    var firstAvailable = product.variants && product.variants.find(function (v) { return v.available; });
    var variant = firstAvailable || (product.variants && product.variants[0]);
    if (!variant) return '';
    var img = imageUrl(product.featured_image, 400);
    return (
      '<article class="cart-drawer__rec-tile">' +
        '<a href="' + escapeHtml(marketUrl(product.url)) + '" class="cart-drawer__rec-image-link" tabindex="-1" aria-hidden="true">' +
          (img ? '<img src="' + escapeHtml(img) + '" alt="' + escapeHtml(product.title) + '" class="cart-drawer__rec-image" loading="lazy" width="160" height="160">' : '') +
        '</a>' +
        '<div class="cart-drawer__rec-info">' +
          '<h3 class="cart-drawer__rec-title">' +
            '<a href="' + escapeHtml(marketUrl(product.url)) + '">' + splitTitle(product.title) + '</a>' +
          '</h3>' +
          '<p class="cart-drawer__rec-price">' + priceMarkup(variant.price, variant.compare_at_price, salePct, product) + '</p>' +
        '</div>' +
        '<button type="button" class="cart-drawer__rec-add" data-cart-rec-add data-variant-id="' + variant.id + '">Add</button>' +
      '</article>'
    );
  }

  function renderInto(container, products) {
    if (!products || !products.length) {
      container.innerHTML = '';
      return;
    }
    var salePct = parseInt(container.dataset.salePct1, 10) || 0;
    var tiles = products.slice(0, REC_LIMIT).map(function (p) { return buildTile(p, salePct); }).filter(Boolean).join('');
    container.innerHTML =
      '<h2 class="cart-drawer__rec-heading">You may also like</h2>' +
      '<div class="cart-drawer__rec-grid">' + tiles + '</div>';
  }

  var REC_LIMIT = 2;

  function fetchRecs(productId, intent) {
    var url = marketRoot() + 'recommendations/products.json?product_id=' + encodeURIComponent(productId) +
              '&limit=' + REC_LIMIT + '&intent=' + encodeURIComponent(intent);
    return fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function dedupBy(products, key) {
    var seen = {};
    return products.filter(function (p) {
      var k = p[key];
      if (seen[k]) return false;
      seen[k] = true;
      return true;
    });
  }

  function readFallback() {
    var el = document.getElementById('cart-rec-fallback');
    if (!el) return { country: null, products: [] };
    try {
      var parsed = JSON.parse(el.textContent);
      return { country: parsed.country || null, products: parsed.products || [] };
    } catch (e) { return { country: null, products: [] }; }
  }

  function isHidableByTag(product, country) {
    if (!country) return false;
    var tags = product.tags;
    if (!tags) return false;
    // Shopify recs API returns tags as either an array (newer) or
    // comma-separated string (legacy). Normalize.
    if (typeof tags === 'string') tags = tags.split(',').map(function (t) { return t.trim(); });
    var oos = 'Out of stock in ' + country;
    var force = 'Force out of stock in ' + country;
    return tags.indexOf(oos) >= 0 || tags.indexOf(force) >= 0;
  }

  /* `_requires_engraving` products can only be added through the vessel
     customizer (with a design). `_scent_society_only` ones only for a known
     member (the gate's window.EBMembership) — a non-member's add would only be
     refused at checkout. The server fallback list drops both outright. Both
     the recs API and the fallback JSON carry `tags`. */
  function isMemberOnlyForViewer(product) {
    if (!hasTag(product, '_scent_society_only')) return false;
    return !(window.EBMembership && window.EBMembership.member === true);
  }

  function filterRecs(products, inCart, country) {
    return products.filter(function (p) {
      if (inCart[p.id]) return false;
      if (!p.variants || !p.variants.some(function (v) { return v.available; })) return false;
      if (isHidableByTag(p, country)) return false;
      if (hasTag(p, '_requires_engraving') || isMemberOnlyForViewer(p)) return false;
      return true;
    });
  }

  /* Recommendation results per market + seed. In memory for this page, in
     sessionStorage (30 min) across pages. Only the API response is cached;
     the in-cart filter is applied fresh on every render. */
  var REC_CACHE_TTL_MS = 30 * 60 * 1000;
  var recMemo = {};

  function readCachedRecs(cacheKey) {
    try {
      var raw = sessionStorage.getItem(cacheKey);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || Date.now() - entry.t > REC_CACHE_TTL_MS) return null;
      return entry.products || null;
    } catch (e) { return null; }
  }

  function writeCachedRecs(cacheKey, products) {
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify({ t: Date.now(), products: products }));
    } catch (e) { /* storage unavailable / full — memory cache still works */ }
  }

  function getRecs(seed) {
    var cacheKey = 'eb:cart-recs:' + marketRoot() + ':' + seed;
    if (recMemo[cacheKey]) return recMemo[cacheKey];
    var cached = readCachedRecs(cacheKey);
    if (cached) {
      recMemo[cacheKey] = Promise.resolve(cached);
      return recMemo[cacheKey];
    }
    recMemo[cacheKey] = Promise.all([
      fetchRecs(seed, 'complementary').catch(function () { return null; }),
      fetchRecs(seed, 'related').catch(function () { return null; })
    ]).then(function (results) {
      var comp = (results[0] && results[0].products) || [];
      var rel = (results[1] && results[1].products) || [];
      var merged = dedupBy(comp.concat(rel), 'id');
      // Don't pin an empty answer from a failed request for the session.
      if (results[0] || results[1]) writeCachedRecs(cacheKey, merged);
      else delete recMemo[cacheKey];
      return merged;
    });
    return recMemo[cacheKey];
  }

  function inCartIds(container) {
    var inCart = {};
    (container.dataset.cartProductIds || '').split(',').forEach(function (id) {
      if (id) inCart[id] = true;
    });
    return inCart;
  }

  function hydrateOne(container) {
    if (container.dataset.recHydrated === 'true') return;
    if (container.dataset.recFetching === 'true') return;
    container.dataset.recFetching = 'true';
    var seed = container.dataset.seedProductId;
    if (!seed) {
      delete container.dataset.recFetching;
      return;
    }
    var fallback = readFallback();
    var country = fallback.country;
    var inCart = inCartIds(container);
    getRecs(seed).then(function (merged) {
      var filtered = filterRecs(merged, inCart, country);
      /* Empty after filtering → fall back to server-rendered bestsellers
         (excluding anything already in cart). The fallback is itself
         already filtered server-side for market visibility, in-stock
         and country-tag gating. */
      if (filtered.length === 0) {
        filtered = filterRecs(fallback.products, inCart, country);
      }
      if (filtered.length === 0) {
        /* Truly nothing to show. Clear placeholder but DON'T lock —
           a later cart change (e.g. removing an item that was
           crowding the recs) gets another chance. */
        container.innerHTML = '';
        return;
      }
      container.dataset.recHydrated = 'true';
      renderInto(container, filtered);
    }).catch(function (err) {
      console.error('cart-drawer-recommendations: hydrate failed', err);
      container.innerHTML = '';
    }).then(function () {
      delete container.dataset.recFetching;
    });
  }

  function drawerIsOpen() {
    var drawer = document.querySelector('cart-drawer');
    return !!(drawer && drawer.classList.contains('active'));
  }

  /* Only while the drawer is open — a closed drawer's placeholder is filled
     when it opens (see watchDrawerOpen). */
  function hydrateAll() {
    if (!drawerIsOpen()) return;
    document.querySelectorAll('[data-cart-recommendations]').forEach(hydrateOne);
  }

  /* ATC handler. Mirrors product-form.js approach: POST to /cart/add.js
     with sections requested, then hand the parsed state to the existing
     cartDrawer.renderContents() so the drawer re-renders identically to
     a PDP add. */
  function onAddClick(e) {
    var btn = e.target.closest('[data-cart-rec-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var variantId = btn.dataset.variantId;
    if (!variantId) return;
    var staleError = btn.closest('.cart-drawer__rec-tile') &&
      btn.closest('.cart-drawer__rec-tile').querySelector('.cart-drawer__rec-error');
    if (staleError) staleError.remove();
    /* Whether focus was in the drawer — read before disabling the button,
       which drops focus. See the refocus after the swap below. */
    var drawerEl = document.querySelector('cart-drawer');
    var hadFocus = !!(drawerEl && drawerEl.contains(document.activeElement));
    btn.disabled = true;
    btn.textContent = 'Adding…';
    fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      })
    }).then(function (r) { return r.text(); }).then(function (text) {
      var parsed = JSON.parse(text);
      if (parsed.status && parsed.status >= 400) {
        /* Member not signed in on the shop: see assets/scent-society-gate.js. */
        if (window.EBMembership && window.EBMembership.recoverShopSignIn &&
            window.EBMembership.recoverShopSignIn(parsed.description)) return;
        var addError = new Error(parsed.description || parsed.message || 'add to cart failed');
        addError.userMessage = parsed.description || parsed.message;
        throw addError;
      }
      /* Manually swap drawer sections. We don't use cartDrawer.renderContents()
         here — it's designed for PDP-side ATC (open closed drawer + show
         contents) and calling it from inside an already-open drawer
         drops the inner section swap on the empty → non-empty transition.
         We also need to toggle is-empty off the cart-drawer host since
         Dawn's renderContents only handles a stale .drawer__inner.is-empty.

         We swap ONLY .drawer__inner's contents, not all of #CartDrawer —
         that preserves the sibling #CartDrawer-Overlay element so its
         constructor-bound click-to-close listener (cart-drawer.js)
         keeps working. Replacing #CartDrawer.innerHTML wholesale would
         destroy the overlay and break click-outside-to-close. */
      var cartDrawer = document.querySelector('cart-drawer');
      var drawerSection = parsed.sections && parsed.sections['cart-drawer'];
      if (drawerSection) {
        var sectionDoc = new DOMParser().parseFromString(drawerSection, 'text/html');
        var newDrawerInner = sectionDoc.querySelector('#CartDrawer .drawer__inner');
        var ourDrawerInner = document.querySelector('#CartDrawer .drawer__inner');
        if (newDrawerInner && ourDrawerInner) {
          /* Preload images from the new section so the cache is warm by the
             time we swap. Without this, every <img> in the swapped DOM goes
             through src-set → cache-check → decode → paint, which is fast
             on a cache hit but still shows a brief flash on cart change. */
          var imgUrls = Array.prototype.map.call(
            newDrawerInner.querySelectorAll('img[src]'),
            function (img) { return img.getAttribute('src'); }
          );
          imgUrls.forEach(function (src) { var i = new Image(); i.src = src; });
          ourDrawerInner.innerHTML = newDrawerInner.innerHTML;
        }
      }
      if (cartDrawer) {
        cartDrawer.classList.remove('is-empty');
      }
      var bubbleSection = parsed.sections && parsed.sections['cart-icon-bubble'];
      if (bubbleSection) {
        var bubbleDoc = new DOMParser().parseFromString(bubbleSection, 'text/html');
        var newBubble = bubbleDoc.querySelector('.shopify-section');
        var oldBubble = document.getElementById('cart-icon-bubble');
        if (newBubble && oldBubble) {
          oldBubble.innerHTML = newBubble.innerHTML;
        }
      }
      /* Notify pubsub subscribers (notably cart-drawer-gift.js) that the
         cart changed so they can react — e.g. auto-add the free Hand Mist
         once a rec-tile ATC pushes the cart over the $75 threshold.
         Without this publish, the gift subscriber never fires for the
         recs ATC path and the gift line is never added. */
      try {
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-recommendations',
            cartData: parsed
          });
        }
      } catch (e) {}
      /* The swap removed the tile's button (the recs re-hydrate empty) and
         left the drawer's focus trap pointing at detached nodes. Re-trap and
         land on the line just added — its key starts with the variant id —
         falling back to the close button (cart-drawer.js). */
      if (cartDrawer && typeof cartDrawer.refocusAfterRender === 'function') {
        cartDrawer.refocusAfterRender(hadFocus, [
          '.cart-item[data-key^="' + parseInt(variantId, 10) + ':"] .cart-item__name'
        ]);
      }
      /* Re-hydrate the recs widget against the freshly-rendered cart's
         seed (the just-added product). Two RAFs to ensure the DOM swap
         has landed and the new placeholder is in the DOM. */
      requestAnimationFrame(function () {
        requestAnimationFrame(hydrateAll);
      });
    }).catch(function (err) {
      btn.disabled = false;
      btn.textContent = 'Add';
      console.error('cart-drawer-recommendations: ATC failed', err);
      showAddError(btn, err && err.userMessage);
    });
  }

  /* A refused add used to reset the button silently. Show Shopify's reason
     (e.g. a stock cap) under the tile, using the drawer's line-item error
     classes. Inline rather than #CartDrawer-CartErrors, which sits in the
     cart form and is hidden while the cart is empty (the Bestsellers tiles). */
  function showAddError(btn, message) {
    var tile = btn.closest('.cart-drawer__rec-tile');
    if (!tile) return;
    var box = tile.querySelector('.cart-drawer__rec-error');
    if (!box) {
      box = document.createElement('div');
      box.className = 'cart-item__error cart-drawer__rec-error';
      box.setAttribute('role', 'alert');
      box.appendChild(document.createElement('small')).className = 'cart-item__error-text';
      tile.appendChild(box);
    }
    box.firstChild.textContent = message || (window.cartStrings && window.cartStrings.error) ||
      'There was an error while updating your cart. Please try again.';
  }

  document.addEventListener('click', onAddClick);

  /* Hydrate when the drawer opens (header icon, add to cart, the /cart
     redirect) — watched on the drawer's `active` class so every opener is
     covered without knowing about it — and on cartUpdate pubsub events from
     cart.js / cart-drawer-subscriptions while it is open. */
  function watchDrawerOpen() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.dataset.recOpenObserver === 'true') return;
    drawer.dataset.recOpenObserver = 'true';
    var wasOpen = drawer.classList.contains('active');
    new MutationObserver(function () {
      var open = drawer.classList.contains('active');
      if (open && !wasOpen) {
        requestAnimationFrame(function () { requestAnimationFrame(hydrateAll); });
      }
      wasOpen = open;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    if (wasOpen) hydrateAll();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDrawerOpen);
  } else {
    watchDrawerOpen();
  }

  /* Re-hydrate after cart updates (the drawer's HTML is replaced, so the
     placeholder is fresh and needs re-fetching). Two channels:
     - PUB_SUB_EVENTS.cartUpdate from cart.js (qty +/-, native remove)
     - cart-drawer:rerender CustomEvent from cart-drawer-subscriptions.js */
  document.addEventListener('cart-drawer:rerender', function () {
    setTimeout(hydrateAll, 50);
  });

  function trySubscribePubsub() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return false;
    /* Double rAF — not setTimeout — because product-form.js publishes
       cartUpdate BEFORE calling cart-drawer.renderContents(), which now
       wraps its DOM swap in a requestAnimationFrame (INP fix). A naive
       setTimeout(50) can race that rAF: on the initial PDP ATC the
       placeholder doesn't exist in the drawer yet (cart was empty), and
       if paint is busy enough that the timer fires before the swap lands,
       hydrateAll runs against stale DOM and bails. Two rAFs guarantees
       we run on the frame AFTER renderContents has swapped the section. */
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      requestAnimationFrame(function () { requestAnimationFrame(hydrateAll); });
    });
    return true;
  }
  if (!trySubscribePubsub()) {
    /* pubsub.js / constants.js may not be parsed yet; retry after DOM ready. */
    document.addEventListener('DOMContentLoaded', trySubscribePubsub);
  }

  /* Robust backstop for the "recs don't appear on the first add" race.
     On a cart change the drawer is re-rendered by SEVERAL independent
     components, not just one:
       - product-form.js  — the ATC swap (synchronous, one rAF)
       - cart-drawer-gift.js / cart-drawer-subscriptions.js — fetch /cart and
         swap .drawer__inner ASYNCHRONOUSLY, then dispatch cart-drawer:rerender
     On the FIRST add (empty → 1 item) the gift/subscription reconcile fires for
     the first time, so the placeholder gets swapped TWICE in quick succession.
     The rAF/pubsub/CustomEvent triggers above can hydrate the first placeholder
     only to have the async second swap replace it with a fresh, un-hydrated one
     — leaving an empty grid. (cart-drawer-gift.js documents this same race for
     the qty-stepper path, which it fixed by folding into a single render; the
     first-add path comes through product-form.js and isn't covered there.)

     A MutationObserver keyed on the PLACEHOLDER being (re)inserted closes the
     race regardless of which component swapped the DOM, how many times, or in
     what order: whenever a swapped-in subtree contains [data-cart-recommendations]
     we (re)hydrate it. It deliberately fires only when the placeholder NODE is
     added — our own tile rendering adds children INSIDE the placeholder, not a
     new placeholder, so this never self-triggers a loop. */
  function watchForRecsPlaceholder() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.dataset.recObserver === 'true') return;
    drawer.dataset.recObserver = 'true';

    var scheduled = false;
    var schedule = function () {
      if (scheduled) return;
      scheduled = true;
      /* Double rAF so we run AFTER the swap that inserted the placeholder has
         fully landed (mirrors the pubsub trigger's timing). */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { scheduled = false; hydrateAll(); });
      });
    };

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (
            (node.matches && node.matches('[data-cart-recommendations]')) ||
            (node.querySelector && node.querySelector('[data-cart-recommendations]'))
          ) {
            schedule();
            return;
          }
        }
      }
    });
    observer.observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForRecsPlaceholder);
  } else {
    watchForRecsPlaceholder();
  }
})();
